export interface Bounds {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface Point {
	x: number;
	y: number;
}

export interface ICollidable {
	/**
	 * Returns the bounding box for collision detection
	 */
	getBounds(): Bounds;

	/**
	 * Called when a collision occurs with another collidable
	 * @param other The other object involved in the collision
	 */
	onCollision(other: ICollidable): void;

	/**
	 * Returns whether this object should be checked for collisions
	 * (e.g., collected items return false)
	 */
	isActive(): boolean;

	/**
	 * Optional type identifier for collision handling
	 */
	readonly collisionType: string;
}

/**
 * Collision utility functions
 */
export const CollisionUtils = {
	/** Get center point of bounds */
	getCenter(bounds: Bounds): Point {
		return {
			x: bounds.x + bounds.width / 2,
			y: bounds.y + bounds.height / 2,
		};
	},

	/** Get bottom-center point (center of gravity) */
	getCenterOfGravity(bounds: Bounds): Point {
		return {
			x: bounds.x + bounds.width / 2,
			y: bounds.y + bounds.height,
		};
	},

	/** Check if a point is inside bounds */
	pointInBounds(point: Point, bounds: Bounds): boolean {
		return (
			point.x >= bounds.x &&
			point.x <= bounds.x + bounds.width &&
			point.y >= bounds.y &&
			point.y <= bounds.y + bounds.height
		);
	},

	/** Check if center of A is inside B */
	centerInBounds(a: Bounds, b: Bounds): boolean {
		const center = CollisionUtils.getCenter(a);
		return CollisionUtils.pointInBounds(center, b);
	},

	/** Check if center of gravity of A is inside B */
	gravityInBounds(a: Bounds, b: Bounds): boolean {
		const cog = CollisionUtils.getCenterOfGravity(a);
		return CollisionUtils.pointInBounds(cog, b);
	},

	/** Standard AABB collision check */
	aabbCollision(a: Bounds, b: Bounds): boolean {
		return (
			a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
		);
	},

	/** Distance between centers */
	centerDistance(a: Bounds, b: Bounds): number {
		const centerA = CollisionUtils.getCenter(a);
		const centerB = CollisionUtils.getCenter(b);
		const dx = centerA.x - centerB.x;
		const dy = centerA.y - centerB.y;
		return Math.sqrt(dx * dx + dy * dy);
	},
};
